import React, { useState } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import UserManagementTab from './components/UserManagementTab';
import SecurityConfigTab from './components/SecurityConfigTab';
import DatabaseSettingsTab from './components/DatabaseSettingsTab';
import IntegrationsTab from './components/IntegrationsTab';
import AuditLogsTab from './components/AuditLogsTab';

const SystemSettings = () => {
  const [activeTab, setActiveTab] = useState('users');

  // Mock user data for header
  const currentUser = {
    id: 1,
    name: "Admin User",
    email: "admin@cyberguard.gov",
    role: "Administrator",
    permissions: ["admin", "view_dashboard", "manage_users", "view_reports", "view_settings"]
  };

  // Mock incident counts for header
  const incidentCounts = {
    critical: 2,
    high: 5,
    medium: 12,
    low: 8
  };

  const tabs = [
    {
      id: 'users',
      label: 'User Management',
      icon: 'Users',
      description: 'Manage user accounts and permissions'
    },
    {
      id: 'security',
      label: 'Security & SSL',
      icon: 'Shield',
      description: 'Configure security policies and certificates'
    },
    {
      id: 'database',
      label: 'Database',
      icon: 'Database',
      description: 'Database connections and backup settings'
    },
    {
      id: 'integrations',
      label: 'Integrations',
      icon: 'Plug',
      description: 'Third-party system integrations'
    },
    {
      id: 'audit',
      label: 'Audit Logs',
      icon: 'FileText',
      description: 'System activity and security logs'
    }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'users':
        return <UserManagementTab />;
      case 'security':
        return <SecurityConfigTab />;
      case 'database':
        return <DatabaseSettingsTab />;
      case 'integrations':
        return <IntegrationsTab />;
      case 'audit':
        return <AuditLogsTab />;
      default:
        return <UserManagementTab />;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header user={currentUser} incidentCounts={incidentCounts} />
      <main className="nav-offset">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Breadcrumb />
          
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-2">
              <div className="p-2 bg-primary rounded-lg">
                <Icon name="Settings" size={24} color="white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">System Settings</h1>
                <p className="text-muted-foreground">Configure system-wide settings and administrative controls</p>
              </div>
            </div>
          </div>

          {/* Settings Navigation */}
          <div className="bg-card rounded-lg border border-border mb-8">
            {/* Desktop Tabs */}
            <div className="hidden md:block border-b border-border">
              <nav className="flex space-x-8 px-6" aria-label="Settings tabs">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                      activeTab === tab?.id
                        ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-border-active'
                    }`}
                  >
                    <Icon name={tab?.icon} size={18} />
                    <span>{tab?.label}</span>
                  </button>
                ))}
              </nav>
            </div>

            {/* Mobile Dropdown */}
            <div className="md:hidden p-4 border-b border-border">
              <div className="relative">
                <select
                  value={activeTab}
                  onChange={(e) => setActiveTab(e?.target?.value)}
                  className="w-full p-3 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                >
                  {tabs?.map((tab) => (
                    <option key={tab?.id} value={tab?.id}>
                      {tab?.label}
                    </option>
                  ))}
                </select>
                <Icon 
                  name="ChevronDown" 
                  size={16} 
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground pointer-events-none" 
                />
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                {tabs?.find(tab => tab?.id === activeTab)?.description}
              </p>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {renderTabContent()}
            </div>
          </div>

          {/* System Information Footer */}
          <div className="bg-card rounded-lg border border-border p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex items-center space-x-3">
                <Icon name="Server" size={20} className="text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium text-foreground">System Version</p>
                  <p className="text-xs text-muted-foreground">CyberGuard v2.1.4</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Icon name="Clock" size={20} className="text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium text-foreground">Last Updated</p>
                  <p className="text-xs text-muted-foreground">September 15, 2025</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Icon name="Shield" size={20} className="text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium text-foreground">Security Status</p>
                  <p className="text-xs text-success">All systems secure</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SystemSettings;